// src/components/promo/PromoRedeemModal.js
//
// Two-phase promo-code redemption surface:
//   • "offer"   — a bottom-sheet with the campaign offer + Claim CTA.
//   • "success" — a centered celebratory card with the balance bump + key progress.
//
// Dark/light aware via theme.colors.* and theme.palette.*.
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  Drawer,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { keyframes } from "@emotion/react";
import CloseIcon from "@mui/icons-material/Close";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import EventIcon from "@mui/icons-material/Event";
import party from "party-js";

import { useAuth } from "../../contexts/AuthContext";
import usePointsReceipts from "../../hooks/points/usePointsReceipts";
import { asInt } from "../../utils/points/format";
import { APP_SHELL_LAYOUT } from "../../constants/constants";
import { normalizePromoCode } from "../../utils/promoRedemption";
import { previewPromoCode, redeemPromoCode } from "../../services/promoCodeService";

const rise = keyframes`
  0% { transform: translateY(10px) scale(0.98); opacity: 0; }
  100% { transform: translateY(0) scale(1); opacity: 1; }
`;

const iconPop = keyframes`
  0% { transform: scale(0.6); opacity: 0; }
  55% { transform: scale(1.12); opacity: 1; }
  100% { transform: scale(1); opacity: 1; }
`;

/** Map an HttpsError (or generic error) to friendly copy. */
function friendlyError(error) {
  const rawCode = String(error?.code || "");
  const code = rawCode.includes("/") ? rawCode.split("/").pop() : rawCode;
  switch (code) {
    case "unauthenticated":
      return "Sign in to redeem a code.";
    case "invalid-argument":
      return "Enter a valid promo code.";
    case "not-found":
      return "That code isn't valid.";
    case "already-exists":
      return "You already redeemed this code.";
    case "resource-exhausted":
      return "This code has reached its limit.";
    case "failed-precondition":
      return error?.message || "This code isn't active.";
    default:
      return error?.message || "Something went wrong. Please try again.";
  }
}

/** Copy for a non-redeemable preview verdict. */
function reasonCopy(reason) {
  switch (reason) {
    case "expired":
      return "This offer has expired.";
    case "not_started":
      return "This offer isn't available yet.";
    case "limit":
      return "This offer has reached its limit.";
    case "already_redeemed":
      return "You've already claimed this offer.";
    default:
      return "This offer isn't active right now.";
  }
}

function validityLabel(endAtMs) {
  if (!endAtMs) return "No expiry — claim anytime";
  try {
    const when = new Date(endAtMs).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
    });
    return `Valid through ${when}`;
  } catch {
    return "Limited-time offer";
  }
}

export default function PromoRedeemModal({ open, code, onClose }) {
  const { currentUser } = useAuth();
  const uid = currentUser?.uid || null;
  const { pointsTotal } = usePointsReceipts(uid);

  // A deep-link (?promo=CODE) opens straight on the offer; no code opens the
  // manual-entry field first.
  const isManualEntry = !code;

  const [phase, setPhase] = useState(code ? "offer" : "entry"); // "entry" | "offer" | "success"
  const [activeCode, setActiveCode] = useState(code || "");
  const [manualCode, setManualCode] = useState("");
  const [entryError, setEntryError] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(true);
  const [offer, setOffer] = useState(null);
  const [previewError, setPreviewError] = useState(null);

  const [claiming, setClaiming] = useState(false);
  const [claimError, setClaimError] = useState(null);
  const [result, setResult] = useState(null);

  // Balance bump: snapshot the total the instant we claim, then animate up.
  const [beforePoints, setBeforePoints] = useState(0);
  const [displayPoints, setDisplayPoints] = useState(0);

  const successCardRef = useRef(null);
  const pointsAtOpenRef = useRef(0);

  // Keep the freshest known total handy for the "before" snapshot.
  useEffect(() => {
    pointsAtOpenRef.current = asInt(pointsTotal, 0);
  }, [pointsTotal]);

  // Reset to the starting phase each time the modal (re)opens.
  useEffect(() => {
    if (!open) return;
    setClaimError(null);
    setResult(null);
    setPreviewError(null);
    setEntryError(null);
    setManualCode("");
    if (code) {
      setActiveCode(code);
      setPhase("offer");
    } else {
      setActiveCode("");
      setPhase("entry");
    }
  }, [open, code]);

  // Fetch the offer preview whenever we land on the offer phase with a code.
  useEffect(() => {
    if (!open || phase !== "offer" || !activeCode) return undefined;

    let alive = true;
    setPreviewLoading(true);
    setPreviewError(null);
    setOffer(null);

    previewPromoCode(activeCode)
      .then((data) => {
        if (!alive) return;
        if (data?.ok) setOffer(data);
        else setPreviewError("That code isn't valid.");
      })
      .catch((err) => {
        if (!alive) return;
        setPreviewError(friendlyError(err));
      })
      .finally(() => {
        if (alive) setPreviewLoading(false);
      });

    return () => {
      alive = false;
    };
  }, [open, phase, activeCode]);

  const points = asInt(offer?.points, 0);
  const awardedPoints = asInt(result?.awardedPoints, 0);
  const afterPoints = beforePoints + awardedPoints;

  const handleApplyManual = useCallback(() => {
    const c = normalizePromoCode(manualCode);
    if (c.length < 4) {
      setEntryError("Enter a valid promo code.");
      return;
    }
    setEntryError(null);
    setActiveCode(c);
    setPhase("offer");
  }, [manualCode]);

  const handleClaim = useCallback(async () => {
    if (claiming || !activeCode) return;
    setClaiming(true);
    setClaimError(null);
    setBeforePoints(pointsAtOpenRef.current);
    try {
      const data = await redeemPromoCode(activeCode);
      if (!data?.ok) throw new Error("Redemption failed");
      setResult(data);
      setPhase("success");
    } catch (err) {
      setClaimError(friendlyError(err));
    } finally {
      setClaiming(false);
    }
  }, [claiming, activeCode]);

  // Count-up animation for the balance bump + confetti on the success card.
  useEffect(() => {
    if (phase !== "success") return undefined;

    const reduceMotion =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (reduceMotion) {
      setDisplayPoints(afterPoints);
      return undefined;
    }

    let raf = 0;
    let start = 0;
    const from = beforePoints;
    const to = afterPoints;
    const durationMs = 900;
    const tick = (ts) => {
      if (!start) start = ts;
      const p = Math.min(1, (ts - start) / durationMs);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplayPoints(Math.round(from + (to - from) * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
      else setDisplayPoints(to);
    };
    raf = requestAnimationFrame(tick);

    const confettiTimer = window.setTimeout(() => {
      const target = successCardRef.current;
      if (target) {
        party.confetti(target, {
          count: party.variation.range(70, 100),
          spread: 80,
          size: party.variation.range(0.9, 1.5),
          shapes: ["star", "circle", "roundedSquare"],
        });
      }
    }, 180);

    return () => {
      cancelAnimationFrame(raf);
      window.clearTimeout(confettiTimer);
    };
  }, [phase, beforePoints, afterPoints]);

  const title = offer?.title || "Promo offer";

  // ── Offer (bottom sheet) ────────────────────────────────────────────────
  const renderOfferBody = () => {
    if (previewLoading) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 6 }}>
          <CircularProgress size={28} sx={(theme) => ({ color: theme.colors.blue })} />
        </Box>
      );
    }

    if (previewError) {
      return (
        <Box sx={{ px: 0.5, pb: 1 }}>
          <Alert severity="error" sx={{ borderRadius: 2 }}>
            {previewError}
          </Alert>
          <Button
            fullWidth
            onClick={isManualEntry ? () => setPhase("entry") : onClose}
            sx={(theme) => ({
              mt: 2,
              py: 1.4,
              borderRadius: 999,
              fontWeight: 800,
              backgroundColor: alpha(theme.palette.text.primary, 0.08),
              color: theme.palette.text.primary,
            })}
          >
            {isManualEntry ? "Try another code" : "Close"}
          </Button>
        </Box>
      );
    }

    const canClaim = Boolean(offer?.redeemable);

    return (
      <Box>
        {/* Header row: campaign chip + close */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
          <Box
            sx={(theme) => ({
              display: "inline-flex",
              alignItems: "center",
              gap: 1,
              px: 1.5,
              py: 0.6,
              borderRadius: 999,
              backgroundColor: alpha(theme.colors.blue, theme.palette.mode === "dark" ? 0.22 : 0.12),
              color: theme.palette.mode === "dark" ? theme.colors.white : theme.colors.blue,
            })}
          >
            <Box sx={(theme) => ({ width: 8, height: 8, borderRadius: "50%", backgroundColor: theme.colors.blue })} />
            <Typography sx={{ fontWeight: 800, fontSize: "0.85rem" }} noWrap>
              {title}
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            size="small"
            aria-label="Close"
            sx={(theme) => ({
              color: theme.palette.text.secondary,
              backgroundColor: alpha(theme.palette.text.primary, 0.06),
              "&:hover": { backgroundColor: alpha(theme.palette.text.primary, 0.12) },
            })}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Offer headline */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
          <Box
            sx={(theme) => ({
              width: 56,
              height: 56,
              flex: "0 0 auto",
              borderRadius: 3,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: `linear-gradient(150deg, ${theme.colors.blue}, ${alpha(theme.colors.blue, 0.72)})`,
              boxShadow: `0 10px 22px ${alpha(theme.colors.blue, 0.4)}`,
            })}
          >
            <WorkspacePremiumIcon sx={(theme) => ({ color: theme.colors.white, fontSize: 30 })} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={(theme) => ({ color: theme.palette.text.secondary, fontWeight: 700, fontSize: "0.85rem" })}>
              You'll get
            </Typography>
            <Typography sx={{ fontWeight: 900, lineHeight: 1.1, fontSize: "1.6rem" }}>
              <Box component="span" sx={(theme) => ({ color: theme.colors.blue })}>
                {points}
              </Box>{" "}
              Insider Points
            </Typography>
          </Box>
        </Box>

        {offer?.description ? (
          <Typography sx={(theme) => ({ color: theme.palette.text.secondary, mb: 1.5, fontSize: "0.92rem" })}>
            {offer.description}
          </Typography>
        ) : null}

        {/* Validity */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <EventIcon sx={(theme) => ({ fontSize: 18, color: theme.palette.text.secondary })} />
          <Typography sx={(theme) => ({ color: theme.palette.text.secondary, fontWeight: 700, fontSize: "0.86rem" })}>
            {validityLabel(offer?.endAtMs)}
          </Typography>
        </Box>

        {/* Code box */}
        <Box
          sx={(theme) => ({
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1.5,
            px: 2,
            py: 1.5,
            mb: 2,
            borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.text.primary, 0.14)}`,
            backgroundColor: alpha(theme.palette.text.primary, 0.04),
          })}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={(theme) => ({
                color: theme.palette.text.secondary,
                fontWeight: 800,
                fontSize: "0.66rem",
                letterSpacing: "0.12em",
              })}
            >
              PROMO CODE
            </Typography>
            <Typography
              sx={{
                fontFamily: "'Roboto Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
                fontWeight: 800,
                fontSize: "1.1rem",
                letterSpacing: "0.14em",
              }}
              noWrap
            >
              {offer?.code || activeCode}
            </Typography>
          </Box>
          <Typography
            sx={(theme) => ({
              color: theme.palette.text.secondary,
              fontSize: "0.72rem",
              fontWeight: 700,
              textAlign: "right",
              maxWidth: 110,
            })}
          >
            One-time use per member
          </Typography>
        </Box>

        {claimError ? (
          <Alert severity="error" sx={{ mb: 1.5, borderRadius: 2 }}>
            {claimError}
          </Alert>
        ) : null}

        {canClaim ? (
          <>
            <Button
              fullWidth
              onClick={handleClaim}
              disabled={claiming}
              sx={(theme) => ({
                py: 1.5,
                borderRadius: 999,
                fontWeight: 900,
                fontSize: "1rem",
                backgroundColor: theme.colors.blue,
                color: theme.colors.white,
                "&:hover": { backgroundColor: alpha(theme.colors.blue, 0.9) },
                "&.Mui-disabled": { backgroundColor: alpha(theme.colors.blue, 0.5), color: theme.colors.white },
              })}
            >
              {claiming ? (
                <CircularProgress size={22} sx={(theme) => ({ color: theme.colors.white })} />
              ) : (
                `Claim ${points} points`
              )}
            </Button>
            <Typography
              sx={(theme) => ({
                mt: 1.5,
                textAlign: "center",
                color: theme.palette.text.secondary,
                fontSize: "0.78rem",
              })}
            >
              Points are added to your balance instantly.
            </Typography>
          </>
        ) : (
          <>
            <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
              {reasonCopy(offer?.reason)}
            </Alert>
            <Button
              fullWidth
              onClick={onClose}
              sx={(theme) => ({
                py: 1.4,
                borderRadius: 999,
                fontWeight: 800,
                backgroundColor: alpha(theme.palette.text.primary, 0.08),
                color: theme.palette.text.primary,
              })}
            >
              Got it
            </Button>
          </>
        )}
      </Box>
    );
  };

  // ── Manual entry (bottom sheet) ─────────────────────────────────────────
  const renderEntryBody = () => (
    <Box>
      {/* Header row: title + close */}
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
          <Box
            sx={(theme) => ({
              width: 44,
              height: 44,
              flex: "0 0 auto",
              borderRadius: 2.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: `linear-gradient(150deg, ${theme.colors.blue}, ${alpha(theme.colors.blue, 0.72)})`,
              boxShadow: `0 8px 18px ${alpha(theme.colors.blue, 0.4)}`,
            })}
          >
            <WorkspacePremiumIcon sx={(theme) => ({ color: theme.colors.white, fontSize: 24 })} />
          </Box>
          <Typography sx={{ fontWeight: 900, fontSize: "1.15rem", lineHeight: 1.2 }}>
            Have a promo code?
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          aria-label="Close"
          sx={(theme) => ({
            color: theme.palette.text.secondary,
            backgroundColor: alpha(theme.palette.text.primary, 0.06),
            "&:hover": { backgroundColor: alpha(theme.palette.text.primary, 0.12) },
          })}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Typography sx={(theme) => ({ color: theme.palette.text.secondary, fontSize: "0.9rem", mb: 2 })}>
        Enter it below to claim bonus Insider Points.
      </Typography>

      <TextField
        fullWidth
        value={manualCode}
        onChange={(e) => {
          setManualCode(e.target.value.toUpperCase());
          if (entryError) setEntryError(null);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") handleApplyManual();
        }}
        placeholder="PROMO CODE"
        error={Boolean(entryError)}
        helperText={entryError || " "}
        inputProps={{
          maxLength: 24,
          autoCapitalize: "characters",
          autoCorrect: "off",
          spellCheck: false,
          style: {
            fontFamily: "'Roboto Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
            fontWeight: 700,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
          },
        }}
        sx={(theme) => ({
          "& .MuiOutlinedInput-root": {
            borderRadius: 2,
            "& fieldset": { borderColor: alpha(theme.palette.text.primary, 0.2) },
            "&:hover fieldset": { borderColor: theme.colors.blue },
            "&.Mui-focused fieldset": { borderColor: theme.colors.blue },
          },
        })}
      />

      <Button
        fullWidth
        onClick={handleApplyManual}
        sx={(theme) => ({
          mt: 0.5,
          py: 1.5,
          borderRadius: 999,
          fontWeight: 900,
          fontSize: "1rem",
          backgroundColor: theme.colors.blue,
          color: theme.colors.white,
          "&:hover": { backgroundColor: alpha(theme.colors.blue, 0.9) },
        })}
      >
        Apply code
      </Button>

      <Button
        fullWidth
        onClick={onClose}
        sx={(theme) => ({
          mt: 1,
          py: 1.1,
          borderRadius: 999,
          fontWeight: 700,
          color: theme.palette.text.secondary,
          "&:hover": { backgroundColor: alpha(theme.palette.text.primary, 0.05) },
        })}
      >
        Maybe later
      </Button>
    </Box>
  );

  // ── Success (centered card) ─────────────────────────────────────────────
  const renderSuccess = () => (
    <Box ref={successCardRef} sx={{ textAlign: "center", px: 0.5 }}>
      <Box
        sx={(theme) => ({
          width: 84,
          height: 84,
          mx: "auto",
          mb: 2,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: `linear-gradient(150deg, ${theme.colors.blue}, ${alpha(theme.colors.blue, 0.7)})`,
          boxShadow: `0 14px 30px ${alpha(theme.colors.blue, 0.45)}`,
          animation: `${iconPop} 520ms cubic-bezier(0.2,0.85,0.28,1.2) 1 both`,
        })}
      >
        <WorkspacePremiumIcon sx={(theme) => ({ color: theme.colors.white, fontSize: 44 })} />
      </Box>

      <Typography sx={{ fontWeight: 900, fontSize: "1.5rem", lineHeight: 1.15 }}>
        You earned {awardedPoints} points!
      </Typography>
      <Typography sx={(theme) => ({ mt: 0.75, color: theme.palette.text.secondary, fontSize: "0.95rem" })}>
        Nice grab — <b>{title}</b> is all yours.
      </Typography>

      {/* Balance bump */}
      <Box
        sx={(theme) => ({
          mt: 2.5,
          px: 2,
          py: 1.75,
          borderRadius: 2,
          border: `1px solid ${alpha(theme.palette.text.primary, 0.14)}`,
          backgroundColor: alpha(theme.palette.text.primary, 0.04),
          animation: `${rise} 420ms ease-out 120ms both`,
        })}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1.25 }}>
          <Typography sx={(theme) => ({ color: theme.palette.text.secondary, fontWeight: 800, fontSize: "1.25rem" })}>
            {beforePoints}
          </Typography>
          <Box component="span" sx={(theme) => ({ color: theme.colors.green, fontWeight: 900, fontSize: "1.25rem" })}>
            →
          </Box>
          <Typography sx={{ fontWeight: 900, fontSize: "1.6rem", lineHeight: 1 }}>{displayPoints}</Typography>
          <Box
            sx={(theme) => ({
              px: 1,
              py: 0.25,
              borderRadius: 999,
              backgroundColor: alpha(theme.colors.green, 0.18),
              color: theme.colors.green,
              fontWeight: 900,
              fontSize: "0.8rem",
            })}
          >
            +{awardedPoints}
          </Box>
        </Box>
        <Typography
          sx={(theme) => ({
            mt: 0.75,
            color: theme.palette.text.secondary,
            fontWeight: 800,
            fontSize: "0.66rem",
            letterSpacing: "0.12em",
          })}
        >
          INSIDER POINTS BALANCE
        </Typography>
      </Box>

      <Button
        fullWidth
        onClick={onClose}
        sx={(theme) => ({
          mt: 2.5,
          py: 1.5,
          borderRadius: 999,
          fontWeight: 900,
          fontSize: "1rem",
          backgroundColor: theme.colors.blue,
          color: theme.colors.white,
          "&:hover": { backgroundColor: alpha(theme.colors.blue, 0.9) },
        })}
      >
        Done
      </Button>
    </Box>
  );

  if (phase === "success") {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        disableScrollLock
        PaperProps={{
          sx: (theme) => ({
            width: { xs: "calc(100% - 40px)", sm: 380 },
            maxWidth: { xs: "calc(100% - 40px)", sm: 380 },
            m: { xs: 2.5, sm: 4 },
            p: 3,
            borderRadius: 3,
            backgroundColor: theme.palette.background.paper,
            backgroundImage: "none",
            border: `1px solid ${alpha(theme.palette.text.primary, 0.12)}`,
            boxShadow: "0 24px 70px rgba(0,0,0,0.34)",
            overflow: "hidden",
          }),
        }}
      >
        {renderSuccess()}
      </Dialog>
    );
  }

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      disableScrollLock
      PaperProps={{
        // Transparent full-width paper; the visual sheet is the inner card so it
        // can float with side margins and clear the fixed bottom nav.
        sx: {
          backgroundColor: "transparent !important",
          backgroundImage: "none !important",
          boxShadow: "none",
          overflow: "visible",
        },
      }}
    >
      <Box
        sx={(theme) => ({
          width: "calc(100% - 32px)",
          maxWidth: 460,
          mx: "auto",
          mb: `calc(${APP_SHELL_LAYOUT.BOTTOM_NAV_OFFSET_CSS} + 16px)`,
          px: 2.5,
          pt: 1.25,
          pb: 2.5,
          borderRadius: "22px",
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          border:
            theme.palette.mode === "dark"
              ? "1px solid rgba(174,194,224,0.22)"
              : `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
          boxShadow: "0 24px 70px rgba(0,0,0,0.34)",
          overflow: "hidden",
        })}
      >
        {/* Drag handle */}
        <Box
          sx={(theme) => ({
            width: 44,
            height: 4,
            borderRadius: 999,
            backgroundColor: alpha(theme.palette.text.primary, 0.25),
            mx: "auto",
            mb: 2,
          })}
        />
        {phase === "entry" ? renderEntryBody() : renderOfferBody()}
      </Box>
    </Drawer>
  );
}
